import React, { useCallback, useEffect, useMemo } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';
import type { AsyncFnReturn } from 'react-use/lib/useAsync';

import { useRefLatest } from '../../hooks/useRefLatest';
import { ReactComponent as PermalinkIcon } from '../../icons/icon-permalink.inline.svg';
import { copyToClipboard } from '../../utils/clipboard';
import { handleError } from '../../utils/common-utils';
import { ThreadOptionsStructure } from './ThreadOptionStructure';

const messages = defineMessages({
  copyLink: { defaultMessage: 'Copier le lien du message', id: 'MuqvnX' },
  copiedLink: { defaultMessage: 'Lien copié dans le presse-papiers', id: 'aCzvO9' },
});

export const CopyLinkOption: React.FC<{
  shortUrl: AsyncFnReturn<() => Promise<string>>;
}> = ({ shortUrl }) => {
  const [{ value, loading, error }, fetch] = shortUrl;
  const icon = useMemo(
    () =>
      loading ? (
        <div className="loader" />
      ) : (
        <PermalinkIcon strokeWidth="0.75" stroke="currentColor" fill="currentColor" width="100%" />
      ),
    [loading],
  );

  useEffect(() => {
    // TODO once yakli copy URL are fixed, make those 2 changes:
    // - useRef to check if the fetch was already called
    // - toast error if any error, in addition to logging it.
    if (!value && !loading && !error) fetch();
    if (error) {
      handleError(error);
    }
  }, [value, loading, fetch, error]);

  const loadingRef = useRefLatest(loading);
  const valueRef = useRefLatest(value);

  const handleCopy = useCallback(() => {
    if (!loadingRef.current) {
      copyToClipboard(valueRef.current);
    }
  }, [loadingRef, valueRef]);

  return window.navigator?.clipboard ? (
    <ThreadOptionsStructure
      title={<FormattedMessage {...messages.copyLink} />}
      subtitle={null}
      icon={icon}
      onClick={handleCopy}
    />
  ) : null;
};
